import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Product from 'src/app/model/product/product';
import { BaseProject } from 'src/app/model/project/base-project';
import { Adjustment } from 'src/app/model/project/adjustment';
import { SubProject } from 'src/app/model/project/sub-project';
import { ProjectsTableComponent } from '../projects-table/projects-table.component';

@Component({
  selector: 'supplier-projects',
  templateUrl: '../projects-table/projects-table.component.html',
  styleUrls: ['../projects-table/projects-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupplierProjectsComponent extends ProjectsTableComponent {
  ngOnInit() {
    super.ngOnInit();
    this.projects = [];
    this.displayedColumns = [
      'project_id',
      'externalSupplierDeadline',
      'currentConfirmedPlacementDate',
      'company',
      'externalSupplierInfo',
      'productType',
      'productSize',
      'externalSupplierEmployee',
      'photo',
      'displayStatus',
      'star',
    ];
  }

  protected getLocalStorageKey(): string {
    return 'SupplierProjectsComponent';
  }

  protected getStatusFilter(): string {
    return 'statusSubstatus/vv,Afhaling,Aanpassing,Concept,Nieuw,In_behandeling/Externe_leverancier';
  }

  public getPageTitle(): string {
    return 'Externe leveranciers';
  }

  // Override
  public getDefaultSort() {
    return 'currentConfirmedPlacementDate';
  }

  // Override
  protected preProcessProjects(projects: SubProject[]): void {
    if (projects == null) {
      return;
    }

    super.preProcessProjects(projects);
    projects.forEach((p) => {
      if (p.type === 'BaseProject') {
        this.preProcessBaseProject(<BaseProject>p);
      } else {
        this.preProcessAdjustment(<Adjustment>p);
      }
    });
  }

  private preProcessBaseProject(baseProject: BaseProject): void {
    if (
      baseProject.project == null ||
      !CollectionHelper.isPopulated(baseProject.project.products)
    ) {
      return;
    }
    baseProject.project.products.forEach((p) => this.preProcessProduct(p));
  }

  private preProcessProduct(product: Product) {
    if (product.productPlacement != null) {
      product.externalSupplierLines = [];
      product.externalSupplierDeadlines = [];
      product.externalSupplierEmployees = [];
      if (product.productPlacement.extraHoogwerker) {
        product.externalSupplierLines.push({
          header: 'Hoogwerker: ',
          line: StringHelper.isEmpty(
            product.productPlacement.extraHoogwerkerDescription
          )
            ? null
            : product.productPlacement.extraHoogwerkerDescription,
          finished: product.productPlacement.extraHoogwerkerDone,
        });
        if (product.productPlacement.extraHoogwerkerEmployee != null) {
          product.externalSupplierEmployees.push(
            product.productPlacement.extraHoogwerkerEmployee
          );
        }
        product.externalSupplierDeadlines.push(
          product.productPlacement.extraHoogwerkerDeadline
        );
      }
      if (product.productPlacement.parkingBan) {
        product.externalSupplierLines.push({
          header: 'Parkeerverbod: ',
          line: StringHelper.isEmpty(
            product.productPlacement.extraInfoParkingBan
          )
            ? null
            : product.productPlacement.extraInfoParkingBan,
          finished: product.productPlacement.parkingBanDone,
        });
        if (product.productPlacement.parkingBanEmployee != null) {
          product.externalSupplierEmployees.push(
            product.productPlacement.parkingBanEmployee
          );
        }
        product.externalSupplierDeadlines.push(
          product.productPlacement.parkingBanDeadline
        );
      }
      if (product.productPlacement.needExtraSupplier) {
        product.externalSupplierLines.push({
          header: 'Extra leverancier: ',
          line: StringHelper.isEmpty(
            product.productPlacement.extraInfoNeedExtraSupplier
          )
            ? null
            : product.productPlacement.extraInfoNeedExtraSupplier,
          finished: product.productPlacement.needExtraSupplierDone,
        });
        if (product.productPlacement.extraSupplierEmployee != null) {
          product.externalSupplierEmployees.push(
            product.productPlacement.extraSupplierEmployee
          );
        }
        product.externalSupplierDeadlines.push(
          product.productPlacement.extraSupplierDeadline
        );
      }
      if (product.productPlacement.needExtraMaterials) {
        product.externalSupplierLines.push({
          header: 'Extra materialen: ',
          line: StringHelper.isEmpty(
            product.productPlacement.extraInfoNeedExtraMaterials
          )
            ? null
            : product.productPlacement.extraInfoNeedExtraMaterials,
          finished: product.productPlacement.needExtraMaterialsDone,
        });
        if (product.productPlacement.extraMaterialsEmployee != null) {
          product.externalSupplierEmployees.push(
            product.productPlacement.extraMaterialsEmployee
          );
        }
        product.externalSupplierDeadlines.push(
          product.productPlacement.extraMaterialsDeadline
        );
      }
    }
  }

  private preProcessAdjustment(adjustment: Adjustment): void {
    adjustment.externalSupplierLines = [];
    adjustment.externalSupplierEmployees = [];
    adjustment.externalSupplierDeadlines = [];
    if (adjustment.extraHoogwerker) {
      adjustment.externalSupplierLines.push({
        header: 'Hoogwerker: ',
        line: StringHelper.isEmpty(adjustment.extraHoogwerkerDescription)
          ? null
          : adjustment.extraHoogwerkerDescription,
        finished: adjustment.extraHoogwerkerDone,
      });
      if (adjustment.extraHoogwerkerEmployee != null) {
        adjustment.externalSupplierEmployees.push(
          adjustment.extraHoogwerkerEmployee
        );
      }
      adjustment.externalSupplierDeadlines.push(
        adjustment.extraHoogwerkerDeadline
      );
    }
    if (adjustment.parkingBan) {
      adjustment.externalSupplierLines.push({
        header: 'Parkeerverbod: ',
        line: StringHelper.isEmpty(adjustment.extraInfoParkingBan)
          ? null
          : adjustment.extraInfoParkingBan,
        finished: adjustment.parkingBanDone,
      });
      if (adjustment.parkingBanEmployee != null) {
        adjustment.externalSupplierEmployees.push(
          adjustment.parkingBanEmployee
        );
      }
      adjustment.externalSupplierDeadlines.push(adjustment.parkingBanDeadline);
    }
    if (adjustment.needExtraSupplier) {
      adjustment.externalSupplierLines.push({
        header: 'Extra leverancier: ',
        line: StringHelper.isEmpty(adjustment.extraInfoNeedExtraSupplier)
          ? null
          : adjustment.extraInfoNeedExtraSupplier,
        finished: adjustment.needExtraSupplierDone,
      });
      if (adjustment.extraSupplierEmployee != null) {
        adjustment.externalSupplierEmployees.push(
          adjustment.extraSupplierEmployee
        );
      }
      adjustment.externalSupplierDeadlines.push(
        adjustment.extraSupplierDeadline
      );
    }
    if (adjustment.needExtraMaterials) {
      adjustment.externalSupplierLines.push({
        header: 'Extra materialen: ',
        line: StringHelper.isEmpty(adjustment.extraInfoNeedExtraMaterials)
          ? null
          : adjustment.extraInfoNeedExtraMaterials,
        finished: adjustment.needExtraMaterialsDone,
      });
      if (adjustment.extraMaterialsEmployee != null) {
        adjustment.externalSupplierEmployees.push(
          adjustment.extraMaterialsEmployee
        );
      }
      adjustment.externalSupplierDeadlines.push(
        adjustment.extraMaterialsDeadline
      );
    }
  }
}
